'use client';

import { createClient } from '@/lib/supabase/client';
import type { WorkItem } from '@/app/components/workData';
import { WORK as LOCAL_WORK } from '@/app/components/workData';

/**
 * Works — the homepage's "Selected Work" carousel.
 *
 * Source of truth is the `works` table; see supabase/migrations for the schema
 * and the storage bucket the panel uploads into.
 *
 * Unlike client_stories there is no seed. When the table is empty the public
 * section falls back to whatever is in workData.ts, which today is a single
 * honest entry — this site. If that file is emptied too, the section removes
 * itself. Nothing here ever invents a project.
 */

export const WORKS_TABLE = 'works';
export const WORKS_BUCKET = 'work-images';

export interface WorkRecord extends WorkItem {
  displayOrder: number;
  isPublished: boolean;
  slug: string;
}

interface WorkRow {
  id: string;
  slug: string;
  name: string;
  kind: string | null;
  description: string | null;
  tags: string[] | null;
  desktop_image: string | null;
  mobile_image: string | null;
  desktop_images: string[] | null;
  mobile_images: string[] | null;
  desktop_video: string | null;
  mobile_video: string | null;
  thumb_images: string[] | null;
  href: string | null;
  display_order: number | null;
  is_published: boolean | null;
}

/** Slugify a project name into the stable natural key. */
export function slugFromName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function rowToWork(row: WorkRow): WorkRecord {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name ?? '',
    kind: row.kind ?? '',
    // Stored as one text field, split on blank lines into the paragraphs the
    // section renders. A textarea is a far better editing surface than a
    // repeating field for two short paragraphs.
    description: (row.description ?? '')
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean),
    tags: row.tags ?? [],
    // Prefer the sets; fall back to the singular columns so a row written by
    // the previous build, or one whose backfill has not run, still renders.
    desktopImages: row.desktop_images?.length
      ? row.desktop_images
      : [row.desktop_image, ...(row.thumb_images ?? [])].filter((v): v is string => Boolean(v)),
    mobileImages: row.mobile_images?.length
      ? row.mobile_images
      : [row.mobile_image].filter((v): v is string => Boolean(v)),
    desktopVideo: row.desktop_video || undefined,
    mobileVideo: row.mobile_video || undefined,
    href: row.href || undefined,
    displayOrder: row.display_order ?? 0,
    isPublished: row.is_published ?? false,
  };
}

export function workToRow(work: Omit<WorkRecord, 'id'>) {
  return {
    slug: work.slug || slugFromName(work.name),
    name: work.name,
    kind: work.kind,
    description: work.description.join('\n\n'),
    tags: work.tags,
    desktop_images: work.desktopImages,
    mobile_images: work.mobileImages,
    // Kept in step so a deploy still running the previous build against this
    // row renders rather than showing an empty frame.
    desktop_image: work.desktopImages[0] ?? '',
    mobile_image: work.mobileImages[0] ?? '',
    thumb_images: work.desktopImages.slice(1),
    desktop_video: work.desktopVideo ?? '',
    mobile_video: work.mobileVideo ?? '',
    href: work.href ?? '',
    display_order: work.displayOrder,
    is_published: work.isPublished,
  };
}

export function emptyWork(): Omit<WorkRecord, 'id'> {
  return {
    slug: '',
    name: '',
    kind: '',
    description: [],
    tags: [],
    desktopImages: [],
    mobileImages: [],
    desktopVideo: '',
    mobileVideo: '',
    href: '',
    displayOrder: 0,
    isPublished: false,
  };
}

const byOrder = (a: WorkRecord, b: WorkRecord) =>
  a.displayOrder - b.displayOrder || a.name.localeCompare(b.name);

export type LoadState = 'ready' | 'fallback';

export interface WorksResult {
  works: WorkRecord[];
  state: LoadState;
  error?: string;
}

/**
 * The file's entries, shaped as records. Exported so the public section can
 * seed its state with them: waiting for Supabase before painting anything left
 * the section absent from the server-rendered HTML and blank for as long as
 * the round trip took, which on a slow network is the whole first impression.
 */
export const localWorkRecords = (): WorkRecord[] =>
  LOCAL_WORK.map((w, i) => ({
    ...w,
    slug: w.id,
    displayOrder: i,
    isPublished: true,
  }));

/**
 * Published projects for the public section. Never rejects. An unconfigured or
 * failing backend falls back to the file, so a database outage cannot blank
 * the section.
 */
export async function loadPublishedWorks(): Promise<WorksResult> {
  const supabase = createClient();
  if (!supabase) return { works: localWorkRecords(), state: 'fallback' };

  try {
    const { data, error } = await supabase
      .from(WORKS_TABLE)
      .select('*')
      .eq('is_published', true)
      .order('display_order', { ascending: true });

    if (error) return { works: localWorkRecords(), state: 'fallback', error: error.message };

    const rows = (data ?? []) as WorkRow[];
    // Table exists but nothing is published yet — show the file's entry rather
    // than an empty frame.
    if (rows.length === 0) return { works: localWorkRecords(), state: 'fallback' };

    return { works: rows.map(rowToWork).sort(byOrder), state: 'ready' };
  } catch (err) {
    return {
      works: localWorkRecords(),
      state: 'fallback',
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/** Every project, published or not — the admin list. */
export async function loadAllWorks(): Promise<WorksResult> {
  const supabase = createClient();
  if (!supabase) {
    return {
      works: [],
      state: 'fallback',
      error:
        'Supabase is not configured. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY to manage Works.',
    };
  }

  try {
    const { data, error } = await supabase
      .from(WORKS_TABLE)
      .select('*')
      .order('display_order', { ascending: true });

    if (error) return { works: [], state: 'fallback', error: error.message };
    return { works: ((data ?? []) as WorkRow[]).map(rowToWork).sort(byOrder), state: 'ready' };
  } catch (err) {
    return {
      works: [],
      state: 'fallback',
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

export async function createWork(work: Omit<WorkRecord, 'id'>): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase.from(WORKS_TABLE).insert(workToRow(work));
  return error ? error.message : null;
}

export async function updateWork(id: string, work: Omit<WorkRecord, 'id'>): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase.from(WORKS_TABLE).update(workToRow(work)).eq('id', id);
  return error ? error.message : null;
}

export async function deleteWork(id: string): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return 'Supabase is not configured.';
  const { error } = await supabase.from(WORKS_TABLE).delete().eq('id', id);
  return error ? error.message : null;
}

/**
 * Upload an image or video and return its public URL.
 *
 * The filename is prefixed with the timestamp rather than replacing on
 * collision: two projects that both have a `hero.png` should not overwrite
 * each other, and an edit that swaps an image should not break the version
 * still cached at the old URL.
 */
export async function uploadWorkMedia(file: File): Promise<{ url?: string; error?: string }> {
  const supabase = createClient();
  if (!supabase) return { error: 'Supabase is not configured.' };

  const safe = file.name.toLowerCase().replace(/[^a-z0-9.]+/g, '-');
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safe}`;

  const { error } = await supabase.storage.from(WORKS_BUCKET).upload(path, file, {
    cacheControl: '31536000',
    contentType: file.type || undefined,
    upsert: false,
  });

  if (error) {
    // Supabase reports both of these as a bare "Bucket not found" / "new row
    // violates row-level security policy", which tells an admin nothing about
    // what to do. Both have one specific cause and one specific fix.
    const msg = error.message ?? String(error);
    if (/bucket not found/i.test(msg)) {
      return {
        error:
          'The work-images storage bucket does not exist. Run supabase/migrations/20260819100000_works.sql in the Supabase SQL editor.',
      };
    }
    if (/row-level security|violates|not authorized|jwt/i.test(msg)) {
      return {
        error:
          'Upload was refused: you are not signed in to Supabase. Sign out and back in on the admin login, then retry.',
      };
    }
    if (/exceeded the maximum allowed size|payload too large|413/i.test(msg)) {
      return { error: `${file.name} is over the 25 MB limit for this bucket.` };
    }
    return { error: msg };
  }

  const { data } = supabase.storage.from(WORKS_BUCKET).getPublicUrl(path);
  return { url: data.publicUrl };
}

/**
 * Whether the table and the bucket the panel needs actually exist.
 *
 * Uploading and saving both failed silently when the migration had not been
 * run, because every error surfaced as a toast that vanished. The panel asks
 * this on mount and says plainly what is missing.
 */
export async function checkWorksBackend(): Promise<{
  table: boolean;
  bucket: boolean;
  note?: string;
}> {
  const supabase = createClient();
  if (!supabase) return { table: false, bucket: false, note: 'Supabase is not configured.' };

  const [tableRes, bucketRes] = await Promise.all([
    supabase.from(WORKS_TABLE).select('id', { head: true, count: 'exact' }),
    supabase.storage.from(WORKS_BUCKET).list(undefined, { limit: 1 }),
  ]);

  return {
    table: !tableRes.error,
    bucket: !bucketRes.error,
    note: tableRes.error?.message ?? bucketRes.error?.message,
  };
}
